import { AccessToken, LoginParams } from '../types/auth.type';
import { ValidationUtils } from './validation.utils';
import { nanoid } from 'nanoid';
import { StorageKeys } from '../types/keys.type';
import moment from 'moment';
import { SharedUtils } from './shared.utils';

export enum AuthMethods {
    AUTH_NONE = 'AUTH_NONE',
    AUTH_LEGACY = 'AUTH_LEGACY',
    AUTH_RESERVATION = 'AUTH_RESERVATION',
}
export class AuthUtils {
    public static readonly redirectLogoutUrl = ValidationUtils.validateAndGetEnvVar('REACT_APP_VACASA_URI');
    public static readonly baseUrl = ValidationUtils.validateAndGetEnvVar('REACT_APP_BASE_URL');
    public static readonly loginUrl = `${AuthUtils.baseUrl}/authorize`;
    public static readonly logoutUrl = `${AuthUtils.baseUrl}/logout`;

    public static getLoginUrlParams(loginParams?: LoginParams): URLSearchParams {
        let state: string = `nanoid=${nanoid()}`;

        state += loginParams?.reservationId ? `&reservation_id=${loginParams.reservationId}` : '';
        state += loginParams?.previousView ? `&previous_view=${loginParams.previousView}` : '';

        let redirectUrl = `${ValidationUtils.validateAndGetEnvVar('REACT_APP_REDIRECT_URI')}${loginParams?.refresh ? '/auth' : ''}`;
        const utmQueryParams = AuthUtils.getUTMQueryParams();

        if (utmQueryParams.length > 0) {
            redirectUrl += `?${utmQueryParams.join('&')}`;
        }
        return new URLSearchParams({
            client_id: ValidationUtils.validateAndGetEnvVar('REACT_APP_CLIENT_ID'),
            audience: ValidationUtils.validateAndGetEnvVar('REACT_APP_AUDIENCE'),
            scope: ValidationUtils.validateAndGetEnvVar('REACT_APP_SCOPE'),
            redirect_uri: redirectUrl,
            response_type: 'token id_token',
            nonce: nanoid(),
            state,
            ...loginParams?.args,
        });
    }

    public static getLoginRefreshUrl = (): string => {
        const loginParams: LoginParams = { args: { prompt: 'none' }, refresh: true };
        const params = AuthUtils.getLoginUrlParams(loginParams);
        const url = `${AuthUtils.loginUrl}?${params.toString()}`;

        SharedUtils.setSessionStorageItem(StorageKeys.NONCE, params.get(StorageKeys.NONCE)!);
        SharedUtils.setSessionStorageItem(StorageKeys.STATE, params.get(StorageKeys.STATE)!);

        return url;
    };

    public static goToLogin = () => {
        const params = AuthUtils.getLoginUrlParams();
        const url = `${AuthUtils.loginUrl}?${params.toString()}`;

        SharedUtils.setSessionStorageItem(StorageKeys.NONCE, params.get(StorageKeys.NONCE)!);
        SharedUtils.setSessionStorageItem(StorageKeys.STATE, params.get(StorageKeys.STATE)!);

        window.location.assign(url);
    };

    public static goToIdpLoginNoPrompt = (reservationId?: string, previousView?: string) => {
        const loginParams: LoginParams = { args: { prompt: 'none' }, reservationId, previousView };
        const params = AuthUtils.getLoginUrlParams(loginParams);
        const url = `${AuthUtils.loginUrl}?${params.toString()}`;

        SharedUtils.setSessionStorageItem(StorageKeys.NONCE, params.get(StorageKeys.NONCE)!);
        SharedUtils.setSessionStorageItem(StorageKeys.STATE, params.get(StorageKeys.STATE)!);

        window.location.assign(url);
    };

    public static goToLogout = () => {
        const params = new URLSearchParams({
            redirect_uri: `${AuthUtils.redirectLogoutUrl}/my-trips`,
        });
        const url = `${AuthUtils.logoutUrl}?${params.toString()}`;

        SharedUtils.clearSessionStorage();

        window.location.assign(url);
    };

    public static redirectOnSessionExpired = () => {
        SharedUtils.clearSessionStorage();
        AuthUtils.goToMyTrips();
    };

    public static goToMyTrips = () => {
        window.location.assign(`${AuthUtils.redirectLogoutUrl}/my-trips`);
    };

    public static goToMyTripsLogin = () => {
        const utmQueryParams = AuthUtils.getUTMQueryParams();
        window.location.assign(`${AuthUtils.redirectLogoutUrl}/login?mode=my_trips&path=/my-trips&${utmQueryParams.join('&')}`);
    };

    public static getLegacyTokenFromUrl(): string | null {
        const { hash } = window.location;
        const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
        return hashParams.get('token');
    }

    public static getAuthFromUrl() {
        const { search, hash } = window.location;
        const searchParams = new URLSearchParams(search);
        const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
        const legacyToken = hashParams.get('token') || searchParams.get('token');
        if (legacyToken || hashParams.has('reservation_id') || hashParams.has('access_token') || searchParams.has('error')) {
            return {
                legacyToken,
                reservationId: hashParams.get('reservation_id'),
                error: searchParams.get('error'),
                access_token: hashParams.get('access_token'),
                id_token: hashParams.get('id_token'),
                state: hashParams.get('state'),
                previousView: hashParams.get('previous_view'),
            };
        }

        return null;
    }

    public static getReservationIdFromState(value: string): string | null {
        const searchParams = new URLSearchParams(value);
        if (searchParams.has('reservation_id')) {
            return searchParams.get('reservation_id');
        }

        return null;
    }

    public static getPreviousViewFromState(value: string): string | null {
        const searchParams = new URLSearchParams(value);
        if (searchParams.has('previous_view')) {
            return searchParams.get('previous_view');
        }

        return null;
    }

    public static getAuthFromIframeUrl() {
        const { search, hash } = window.location;

        const searchParams = new URLSearchParams(search);
        const hashParams = new URLSearchParams(hash.replace(/^#/, ''));

        if (searchParams.has('error') || hashParams.has('access_token') || hashParams.has('id_token')) {
            return {
                error: searchParams.get('error'),
                access_token: hashParams.get('access_token'),
                id_token: hashParams.get('id_token'),
                state: hashParams.get('state'),
            };
        }

        return null;
    }

    public static getReferrer(): string | null {
        return SharedUtils.getSessionStorageItem(StorageKeys.REFERRER);
    }

    public static clearStoredAuthData(): void {
        SharedUtils.removeSessionStorageItem(StorageKeys.AUTH_METHOD);
        SharedUtils.removeSessionStorageItem(StorageKeys.LEGACY_TOKEN);
        SharedUtils.removeSessionStorageItem(StorageKeys.ACCESS_TOKEN);
        SharedUtils.removeSessionStorageItem(StorageKeys.RESERVATION_ID);
    }

    public static storeReferrer(referrer: string | null): void {
        if (referrer !== null) {
            SharedUtils.setSessionStorageItem(StorageKeys.REFERRER, referrer);
        }
    }

    public static deleteReferrer(): void {
        SharedUtils.removeSessionStorageItem(StorageKeys.REFERRER);
    }

    public static isInsideIFrame(): boolean {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }

    public static removeUrlFragment() {
        window.history.replaceState(null, ' ', ' ');
    }

    public static isTokenValid(token: AccessToken | null): boolean {
        return token !== null && token && moment().add(60, 'seconds').isBefore(moment.unix(token.exp));
    }

    public static setAuthMethod(authMethod: AuthMethods): void {
        return SharedUtils.setSessionStorageItem(StorageKeys.AUTH_METHOD, authMethod);
    }

    public static getAuthMethod(): AuthMethods {
        return (SharedUtils.getSessionStorageItem(StorageKeys.AUTH_METHOD) as AuthMethods) || AuthMethods.AUTH_NONE;
    }

    public static isLegacySessionValid(): boolean {
        return (
            !!SharedUtils.getSessionStorageItem(StorageKeys.LEGACY_TOKEN) &&
            SharedUtils.getSessionStorageItem(StorageKeys.AUTH_METHOD) === AuthMethods.AUTH_LEGACY
        );
    }

    public static isAccessTokenSessionExpired(): boolean {
        return AuthUtils.getAuthMethod() === AuthMethods.AUTH_RESERVATION && !AuthUtils.isTokenValid(SharedUtils.getDecodedAccessToken());
    }

    public static getUTMQueryParams(): string[] {
        const { search } = window.location;
        const searchParams = new URLSearchParams(search);

        const encode = encodeURIComponent;
        const queryParams = [];

        for (const [key, value] of searchParams) {
            if (key.toLowerCase().startsWith('utm_')) {
                queryParams.push(`${encode(key)}=${encode(value)}`);
            }
        }
        return queryParams;
    }
}
